import React, { useEffect, useState } from 'react';

import { useAsyncFn } from 'react-use';

import { LbBigModal } from '../../../shared/components/modals';
import { SuggestionScenariosInfo } from '../../interfaces/suggestionScenariosInfo';
import { scenariosService } from '../../services/scenariosService';
import { ScanSummaryModalFormValues } from './NgzScanSummaryModal.settings';
import { SelectScansFormSection } from './SelectScansFormSection/SelectScansFormSection';
import { ScenarioSection } from './ScenarioSection/ScenarioSection';
import { NutritionSection } from './NutritionSection/NutritionSection';
import { ComplianceScoreSection } from './ComplianceScoreSection/ComplianceScoreSection';

import { ScanMetadata } from '../../interfaces/scanMetadata';
import { nutritionScanService } from '../../services/scanService/nutritionScanService';
import {
  deleteOptionsByValues,
  transformToOptions,
} from '../../../shared/utils/arrayUtils';
import { Option } from '../../../general-types';
import { getFormDefaultValues } from './utils/getFormDefaultValues';
import { angularizeWithProviders } from '../../../shared/providers';
import { INBODY_TYPE_WITH_DISABLED_SCENARIO } from '../../../shared/constants/inbody-info/InbodyType';

type ScanSummaryModalProps = {
  customerId: number;
  selectedScanId?: number;
  onClose: () => void;
};

const ScanSummaryModal = ({
  customerId,
  selectedScanId,
  onClose,
}: ScanSummaryModalProps) => {
  const [scansMetadata, setScansMetadata] = useState<ScanMetadata[] | null>(null);
  const [inbodyTypeOptions, setInbodyTypeOptions] = useState<Option[] | null>(null);
  const [scanSummary, setScanSummary] = useState<SuggestionScenariosInfo | null>(null);

  const getScansMetadata = async () => {
    const scans = await nutritionScanService.getScanDates(customerId);

    setScansMetadata(scans);
  };

  const getInbodyTypeOptions = async () => {
    const inbodyTypes =
      await nutritionScanService.getCustomerInbodyTypeOptions(customerId);

    const options = transformToOptions({
      list: inbodyTypes,
      valueKey: 'userInbodyType',
      labelKey: 'label',
    });

    const availableOptions = deleteOptionsByValues(options, [
      'ALL',
      ...INBODY_TYPE_WITH_DISABLED_SCENARIO,
    ]);

    setInbodyTypeOptions(availableOptions);
  };

  const getScanSummary = async (data: { startScanId?: number; endScanId?: number }) => {
    const resp = await scenariosService.getSuggestionForUser(customerId, data);

    setScanSummary(resp);
  };

  useEffect(() => {
    getScansMetadata();
    getScanSummary({ endScanId: selectedScanId });
    getInbodyTypeOptions();
  }, [customerId]);

  const [state, handleSubmit] = useAsyncFn(
    async ({ startScanId, endScanId }: ScanSummaryModalFormValues) => {
      if (startScanId && endScanId) {
        await getScanSummary({ startScanId, endScanId });
      }
    },
    [getScanSummary],
  );

  if (!scanSummary || !scansMetadata || !inbodyTypeOptions) {
    return null;
  }

  return (
    <LbBigModal
      open
      title="Scan Results"
      onClose={onClose}
      isLoading={state.loading}
      buttonOptions={{
        noText: 'Close',
        noHandler: onClose,
        yesVisible: false,
      }}
    >
      <SelectScansFormSection
        scansMetadata={scansMetadata}
        inbodyTypeOptions={inbodyTypeOptions}
        defaultValues={getFormDefaultValues(scanSummary)}
        onSubmit={handleSubmit}
      />
      <ScenarioSection
        scenarioCase={scanSummary.scenarioCase}
        scenarioId={scanSummary.scenario_id}
        message={scanSummary.message}
      />
      <NutritionSection
        protein={scanSummary.protein}
        carbs={scanSummary.carbs}
        fats={scanSummary.fats}
      />
      <ComplianceScoreSection scanDetails={scanSummary.scanDetails} />
    </LbBigModal>
  );
};

angularizeWithProviders(ScanSummaryModal, 'ngzScanSummaryModal', {
  customerId: '<',
  selectedScanId: '<',
  onClose: '&',
});

export { ScanSummaryModal };
